import React from 'react';
import LicensePlate from 'components/license_plate/LicensePlate';
import MileageDisplay from 'components/mileage_display/MileageDisplay';

const dateTimeLocales = {
    ar: 'ar-EG-u-nu-arab',
    en: 'en-US',
};

const dateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
};

const formatDate = (timestamp, language = 'en') => {
    if (!timestamp) return '----';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '----';

    return new Intl.DateTimeFormat(
        dateTimeLocales[language] || dateTimeLocales.en,
        dateTimeFormatOptions
    ).format(date);
};

const getProgressBarVariant = (soc) => {
    if (soc >= 75) return 'success';
    if (soc >= 50) return 'warning';
    return 'danger';
};

const clampSoc = (soc) => Math.min(Math.max(soc, 0), 100);

const getVehicleDisplayName = (vehicle = {}) => {
    const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim();
    return vehicle.vehicle_name || vehicle.car_name || makeModel || '--';
};

const formatTranslatedMessage = (template, values) => (
    template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '')
);

const LastUpdated = ({ timestamp, fields, language }) => {
    const date = formatDate(timestamp, language);
    const template = fields?.lastUpdatedAt || `${fields?.lastUpdated || 'Last updated'}: {date}`;

    return (
        <small className="metric-updated-at text-muted" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {formatTranslatedMessage(template, { date })}
        </small>
    );
};

export const VehicleTableRow = ({ vehicle, webtheme, navto, fields, language = 'en' }) => {
    const status = vehicle.vehicle_status || vehicle.status || 'Unknown';
    const soc = vehicle.soc === null || vehicle.soc === undefined || vehicle.soc === '' ? null : Number(vehicle.soc);
    const hasSoc = Number.isFinite(soc);
    const socBarValue = hasSoc ? clampSoc(soc) : 0;
    const vehicleDisplayName = getVehicleDisplayName(vehicle);

    const getTranslatedStatus = () => {
        if (!fields || !status) return status;
        const key = status.toLowerCase();
        return fields[key] || status;
    };

    const displayStatus = getTranslatedStatus();

    return (
        <tr className={`cursor-pointer ${webtheme === 'dark' ? 'dark-row' : ''}`} onClick={() => navto(`/single/vehicle/${vehicle.chassis_number}`, { state: vehicle })}>
            <td data-label={fields ? fields['plate'] : 'Plate'}>
                <LicensePlate plateNumber={vehicle.license_plate} />
            </td>
            <td data-label={fields ? fields['vehicle'] : 'Vehicle'}>
                <div className="d-flex flex-column">
                    <span className="fw-bold">{vehicleDisplayName}</span>
                    <span className="text-muted small">{vehicle.chassis_number}</span>
                </div>
            </td>
            <td data-label={fields ? fields['soc'] : 'SOC'} className="metric-table-cell">
                <div className="metric-cell">
                    {hasSoc ? (
                        <div className="metric-value-row">
                            <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                <div
                                    className={`progress-bar ${getProgressBarVariant(soc) === 'success' ? 'bg-success' : getProgressBarVariant(soc) === 'warning' ? 'bg-warning' : 'bg-danger'}`}
                                    role="progressbar"
                                    style={{ width: `${socBarValue}%` }}
                                    aria-valuenow={socBarValue}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <span className="fw-bold fs-7">{soc}%</span>
                        </div>
                    ) : (
                        <span className="fw-bold fs-7 text-muted">----</span>
                    )}
                    <LastUpdated timestamp={vehicle.timestamp} fields={fields} language={language} />
                </div>
            </td>
            <td data-label={fields ? fields['mileage'] : 'Mileage'} className="metric-table-cell">
                <div className="metric-cell">
                    <MileageDisplay mileage={vehicle.mileage} />
                    <LastUpdated timestamp={vehicle.timestamp} fields={fields} language={language} />
                </div>
            </td>
            <td data-label={fields ? fields['status'] : 'Status'}>
                <div className="d-flex flex-column">
                    <span className={`status-indicator ${vehicle.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                        {displayStatus}
                    </span>
                </div>
            </td>
            <td data-label={fields ? fields['actions'] : 'Action'}>
                <button
                    className='btn btn-outline-secondary action-btn'
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                    onClick={(e) => { e.stopPropagation(); navto(`/complaint/${vehicle.chassis_number}/${vehicle.mileage}`, { state: vehicle.license_plate }) }}
                >
                    {fields?.report || 'Report'}
                </button>
            </td>
        </tr>
    );
};

export default VehicleTableRow;
